import htm from "htm"
import { Value } from "@convertkit/slate"
import { html } from "../../utils/htm"
import defaultSettings from "../settings"

function createSetting({ type, ...options }) {
  const setting = defaultSettings[type](options)
  return { ...options, ...setting }
}

/**
 * Given a JSON value, returns a string representing that value in JSX.
 * This is used to take the children of a <ck-editable/> element and
 * turn it into a JSX value, which can then be easily transformed into
 * a Slate Value.
 *
 * == Example
 * const value = {
 *   type: "ck-email",
 *   children: [
 *     { type: "ck-setting", props: { type: "space" }}
 *   ]
 * }
 *
 * => JSONToJSXString(value)
 * => "<ck-email><ck-setting type='space'></ck-setting></ck-email>"
 *
 * @param {Object} value
 */
function JSONToJSXString(value) {
  if (typeof value === "string") return value

  let props = []
  for (let key in value.props) {
    props.push(`${key}="${value.props[key]}"`)
  }

  return `<${value.type} ${props.join(" ")}>${value.children
    .map(JSONToJSXString)
    .join("")}</${value.type}>`
}

function parseEditableValue({ type: _type, props: _props, children }) {
  const valueString = children.map(JSONToJSXString).join("")

  const slate = new Function(
    "slate",
    `return slate\`<value><document>${valueString}</document></value>\``
  )

  return slate(html)
}

/**
 * Takes a JSX template string and returns an object representing the
 * template.
 *
 * @param {string} template
 */
function parse(template, defaultProps) {
  let settings = []
  let styles = []

  let defaults = {
    settings: {},
    content: {}
  }

  function json(type, props, ...children) {
    switch (type) {
      case "ck-setting": {
        const setting = createSetting(props)
        settings.push(setting)
        defaults.settings[setting.name] =
          defaultProps.settings[setting.name] || setting.value
        // settings don't render anything inside the editor, so return null.
        return null
      }
      case "ck-editable": {
        if (defaultProps.content[props.name]) {
          defaults.content[props.name] = Value.fromJSON(
            defaultProps.content[props.name]
          )
        } else {
          defaults.content[props.name] = parseEditableValue({
            type,
            props,
            children
          })
        }
        break
      }
      case "ck-editor-style": {
        styles.push({ css: children[0], editorOnly: true })
        // styles are extracted and placed at the top of the document,
        // so return null.
        return null
      }
      case "style": {
        // styles marked with data-no-inline attribute will be placed in a separate non-inlineable style
        styles.push({
          css: children[0],
          noInline: Boolean(props && props["data-no-inline"] === "true")
        })
        // styles are extracted and placed at the top of the document,
        // so return null.
        return null
      }
    }

    return { type, props, children }
  }

  const html = htm.bind(json)

  // TODO: Find a better way to evaluate the template JSX. Maybe use
  // DOMParser?
  const jsx = new Function("html", `return html\`${template}\``)
  const elements = jsx(html)

  return { elements, settings, defaults, styles }
}

export default parse
